import { t } from 'i18next';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { State } from 'Libs/redux/reducer';
import Video, { VIDEO_TYPE_TO_TYPE_ID_MAPPING } from 'Models/Video';
import { getTranslatedLabel } from 'Services/language/languageService';
import { selectById } from 'Store/entities/activity/activity.selectors';

export const useActivityData = (activityId: number) => {
  const activity = useSelector((state: State) => selectById(state.entities.activity, activityId));

  const translatedTitle = useMemo(() => (activity ? getTranslatedLabel(activity.title) : null), [activity?.title]);

  const coverImage = useMemo(() => {
    if (!activity?.coverMedia) return null;
    if (activity.coverMedia.video) {
      return new Video({
        url: activity.coverMedia.video.url,
        typeId: VIDEO_TYPE_TO_TYPE_ID_MAPPING[activity.coverMedia.video.type],
      }).getOriginalThumbnailSync();
    }
    return activity.coverMedia.image;
  }, [activity?.coverMedia]);

  const formattedDates = useMemo(() => {
    if (!activity) return { start: '', end: '' };
    return {
      start: new Date(activity.startDate).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
      end: new Date(activity.endDate).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }),
    };
  }, [activity?.startDate, activity?.endDate]);

  const activityTypeMap = {
    campaign: {
      label: t('structural_component:s_activity_list_item.campaign_label'),
      colorLabel: 'CAMPAIGN_MEDIUM',
      backgroundColor: 'CAMPAIGN_LIGHT',
    },
    'breaking-news': {
      label: t('structural_component:s_activity_list_item.breaking_news_label'),
      colorLabel: 'BREAKING_NEWS_MEDIUM',
      backgroundColor: 'BREAKING_NEWS_LIGHT',
    },
  } as const;

  return {
    activity,
    translatedTitle,
    coverImage,
    formattedDates,
    activityTypeMap,
  };
};
